// Display name resolvers — the single source of truth for converting
// internal IDs (meal slugs like "ita_venetian_duck_ragu", ingredient slugs
// like "minced_beef") into human-readable copy that ships to the UI.
//
// Rules:
//   1. Never render a raw ID in JSX. Always go through one of these helpers.
//   2. If an ID has no resolution, the fallback is title-cased + underscores
//      replaced with spaces. This is a graceful degradation, not a feature —
//      a missing name is still a bug worth logging.
//   3. Retailer copy ("Waitrose", "Tesco", …) belongs here too. Retailer-
//      specific text in the app must derive from `retailerLabel(...)` so we
//      never hardcode a single retailer's name.
//
// Used by NomLoop.jsx and any component that renders meal/ingredient/retailer
// names. Centralised so retailer rebinding, copy changes, and ID-to-name
// fixes happen in one place.

const TITLE_CASE = (slug) =>
  String(slug)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const RETAILER_LABELS = {
  aldi: "Aldi",
  lidl: "Lidl",
  tesco: "Tesco",
  asda: "ASDA",
  sainsburys: "Sainsbury's",
  morrisons: "Morrisons",
  waitrose: "Waitrose",
  ms: "M&S",
  "m&s": "M&S",
  mands: "M&S",
  marksandspencer: "M&S",
  marks_and_spencer: "M&S",
};

/**
 * Resolve a meal ID to its display name.
 * @param {string} id — internal meal slug, e.g. "ita_venetian_duck_ragu"
 * @param {Map<string, {name?: string}>|object} mealsById — lookup map
 * @returns {string}
 */
export function mealName(id, mealsById) {
  if (!id) return "";
  const entry =
    mealsById instanceof Map ? mealsById.get(id) : mealsById?.[id];
  if (entry?.name) return entry.name;
  return TITLE_CASE(id);
}

/**
 * Resolve an ingredient ID to its display name.
 * Looks up shoppingList[id].name first (server-provided canonical name),
 * then falls back to title-casing the slug.
 * @param {string} id — ingredient slug, e.g. "minced_beef"
 * @param {object} [shoppingList] — server-computed map keyed by ingredient id
 * @returns {string}
 */
export function ingredientName(id, shoppingList) {
  if (!id) return "";
  const fromList = shoppingList?.[id]?.name;
  if (fromList) return fromList;
  return TITLE_CASE(id);
}

/**
 * Resolve a retailer ID to its display label.
 * @param {string} id — retailer slug, e.g. "waitrose"
 * @param {Array<{id: string, label?: string, name?: string}>} retailers
 * @returns {string}
 */
export function retailerLabel(id, retailers) {
  if (!id) return "";
  const normalized = String(id).trim().toLowerCase();
  const r = (retailers || []).find((x) => String(x.id).trim().toLowerCase() === normalized);
  return r?.label || r?.name || RETAILER_LABELS[normalized] || TITLE_CASE(id);
}

/**
 * Format a swap-suggestion object for display. Centralises the fallback
 * so we never render `Swap suggestion: ita_venetian_duck_ragu`.
 * @param {{message?: string, mealId?: string, mealName?: string}} suggestion
 * @param {Map<string, {name?: string}>|object} mealsById
 * @returns {string}
 */
export function swapSuggestionText(suggestion, mealsById) {
  if (!suggestion) return "";
  if (suggestion.message) return suggestion.message;
  if (suggestion.mealName) return `Try ${suggestion.mealName}`;
  if (suggestion.mealId) return `Try ${mealName(suggestion.mealId, mealsById)}`;
  return "Swap suggestion available";
}


/**
 * Build the user-facing label for an imported recipe source. Keep URLs/domains
 * for linking and searching, but do not render raw hostnames in the UI.
 * @param {string|null|undefined} sourceUrl
 * @param {string|null|undefined} existingName
 * @returns {string}
 */
export function getRecipeSourceDisplayName(sourceUrl, existingName) {
  const cleanExisting = String(existingName || "").trim();

  if (cleanExisting && !cleanExisting.includes(".")) {
    return cleanExisting;
  }

  let host = "";
  if (!sourceUrl) {
    host = cleanExisting.toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
  }

  try {
    if (sourceUrl) host = new URL(sourceUrl).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    host = String(sourceUrl || cleanExisting).toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
  }

  if (host.includes("bbc.co.uk") || host.includes("bbc.com")) {
    return "BBC Food";
  }

  if (host.includes("indianhealthyrecipes.com")) {
    return "indianhealthyrecipes";
  }

  return stripKnownDomainSuffix(host).split(".")[0] || cleanExisting || "source";
}

function stripKnownDomainSuffix(host) {
  return String(host || "")
    .replace(/^www\./i, "")
    .replace(/\.(com|co\.uk|org|net|io|app|food)$/i, "");
}
